import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Box, Typography, Container, Card, CardMedia, CardContent,
    IconButton, Button, Chip, Skeleton, Stack, useTheme, useMediaQuery, Grid
} from '@mui/material';
import DirectionsBikeIcon  from '@mui/icons-material/DirectionsBike';
import DirectionsBusIcon   from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon  from '@mui/icons-material/DirectionsWalk';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import LocationOnIcon      from '@mui/icons-material/LocationOn';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EastIcon             from '@mui/icons-material/East';
import StraightenIcon       from '@mui/icons-material/Straighten';

import { useNavigate }    from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosPublic        from "../api/axiosPublic";
import axiosAuth          from "../api/axiosConfig";
import { useAuth }        from '../context/AuthContext';
import { useCurrency }    from '../context/CurrencyContext';
import { formatCurrency } from '../utils/currency';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';
const BEIGE      = '#F5EDE0';

const HERO_IMAGES = [
    { url: 'https://machanents.com/media/product/RGSN0329-1.jpg?q=80&w=2070&auto=format&fit=crop',
        label: 'Armenian Gorg — Traditional Hand-Woven Carpet' },
    { url: 'https://www.originalarmenia.com/wp-content/uploads/2020/04/armenian-wine-areni.jpg?q=80&w=2070&auto=format&fit=crop',
        label: 'Armenian Wine, Made Of Armenian Grape' }
];

// ── Skeletons ─────────────────────────────────────────────────────────────────
function TourCardSkeleton({ cardWidth, imageHeight }) {
    return (
        <Box sx={{ width: cardWidth, minWidth: cardWidth, borderRadius: '20px', overflow: 'hidden',
            backgroundColor: '#ffffff', border: '1px solid rgba(196,98,45,0.10)',
            boxShadow: '0 3px 10px rgba(44,31,20,0.07)', flexShrink: 0 }}>
            <Box sx={{ padding: '10px', height: imageHeight, overflow: 'hidden' }}>
                <Skeleton variant="rounded" width="100%" height="100%" sx={{ borderRadius: '12px' }} />
            </Box>
            <Box sx={{ p: 2.5, pt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Skeleton variant="rounded" width={72} height={22} sx={{ borderRadius: '4px' }} />
                    <Skeleton variant="circular" width={26} height={26} />
                </Box>
                <Skeleton variant="text" width="80%" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="50%" height={18} />
                <Skeleton variant="text" width="40%" height={18} sx={{ mt: 0.3 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Box>
                        <Skeleton variant="text" width={30} height={14} />
                        <Skeleton variant="text" width={50} height={28} />
                    </Box>
                    <Skeleton variant="rounded" width={90} height={32} sx={{ borderRadius: '20px' }} />
                </Box>
            </Box>
        </Box>
    );
}

// ── Reusable infinite-loop horizontal carousel ────────────────────────────────
function HorizontalCarousel({ items, loading, renderCard, renderSkeleton, cardWidth, gap = 24, skeletonCount = 4 }) {
    const scrollRef   = useRef(null);
    const dragRef     = useRef({ isDragging: false, hasMoved: false, startX: 0, scrollLeft: 0 });
    const autoplayRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [hasMoved,   setHasMoved]   = useState(false);

    const setSize     = items.length;
    const oneSetWidth = setSize * (cardWidth + gap);

    const recentre = useCallback(() => {
        const el = scrollRef.current;
        if (!el || setSize <= 1 || oneSetWidth === 0) return;
        if (el.scrollLeft < oneSetWidth * 0.3)       el.scrollLeft += oneSetWidth;
        else if (el.scrollLeft > oneSetWidth * 1.7)  el.scrollLeft -= oneSetWidth;
    }, [setSize, oneSetWidth]);

    useEffect(() => {
        const el = scrollRef.current;
        if (el && setSize > 1) el.scrollLeft = oneSetWidth;
    }, [items, setSize, oneSetWidth]);

    const stopAutoplay = useCallback(() => {
        if (autoplayRef.current) { clearInterval(autoplayRef.current); autoplayRef.current = null; }
    }, []);

    const startAutoplay = useCallback(() => {
        stopAutoplay();
        if (setSize <= 1) return;
        autoplayRef.current = setInterval(() => {
            const el = scrollRef.current;
            if (!el || dragRef.current.isDragging) return;
            el.scrollBy({ left: cardWidth + gap, behavior: 'smooth' });
            setTimeout(recentre, 450);
        }, 3000);
    }, [setSize, recentre, stopAutoplay, cardWidth, gap]);

    useEffect(() => {
        if (!loading && setSize > 1) startAutoplay();
        return stopAutoplay;
    }, [loading, setSize, startAutoplay, stopAutoplay]);

    const scroll = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        stopAutoplay();
        el.scrollBy({ left: dir === 'left' ? -(cardWidth + gap) : (cardWidth + gap), behavior: 'smooth' });
        setTimeout(() => { recentre(); startAutoplay(); }, 450);
    };

    const handleMouseDown = (e) => {
        stopAutoplay();
        const el = scrollRef.current;
        el.scrollLeft = el.scrollLeft;
        dragRef.current = { isDragging: true, hasMoved: false, startX: e.pageX, scrollLeft: el.scrollLeft };
        setIsDragging(true); setHasMoved(false);
    };
    const handleMouseLeaveOrUp = () => {
        if (dragRef.current.isDragging) {
            dragRef.current.isDragging = false;
            setIsDragging(false);
            setHasMoved(dragRef.current.hasMoved);
            recentre(); startAutoplay();
        }
    };
    const handleMouseMove = (e) => {
        if (!dragRef.current.isDragging) return;
        e.preventDefault();
        const walk = (e.pageX - dragRef.current.startX) * 1.2;
        if (Math.abs(walk) > 5) dragRef.current.hasMoved = true;
        scrollRef.current.scrollLeft = dragRef.current.scrollLeft - walk;
    };

    const arrowStyle = {
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        zIndex: 10, backgroundColor: 'rgba(255,255,255,0.95)', color: TERRACOTTA, opacity: 0,
        transition: 'opacity 0.3s ease, transform 0.2s', boxShadow: '0 4px 16px rgba(44,31,20,0.18)',
        display: { xs: 'none', sm: 'flex' },
        '&:hover': { backgroundColor: 'white', transform: 'translateY(-50%) scale(1.12)' }
    };

    return (
        <Box sx={{ position: 'relative', '&:hover .nav-btn': { opacity: 1 } }}>
            {!loading && items.length > 0 && (
                <>
                    <IconButton className="nav-btn" onClick={() => scroll('left')} sx={{ ...arrowStyle, left: -20 }}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton className="nav-btn" onClick={() => scroll('right')} sx={{ ...arrowStyle, right: -20 }}>
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </>
            )}
            <Box sx={{ maskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)',
                WebkitMaskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)' }}>
                <Box ref={scrollRef}
                     onMouseDown={!loading ? handleMouseDown : undefined}
                     onMouseLeave={!loading ? handleMouseLeaveOrUp : undefined}
                     onMouseUp={!loading ? handleMouseLeaveOrUp : undefined}
                     onMouseMove={!loading ? handleMouseMove : undefined}
                     onTouchEnd={!loading ? () => { recentre(); startAutoplay(); } : undefined}
                     onPointerEnter={!loading ? stopAutoplay : undefined}
                     onPointerLeave={!loading ? () => { if (!dragRef.current.isDragging) startAutoplay(); } : undefined}
                     sx={{ display: 'flex', gap: `${gap}px`, pb: 4, pt: 1, px: '5%', overflowX: 'auto',
                         cursor: loading ? 'default' : isDragging ? 'grabbing' : 'grab',
                         msOverflowStyle: 'none', scrollbarWidth: 'none',
                         '&::-webkit-scrollbar': { display: 'none' } }}>
                    {loading
                        ? Array.from({ length: skeletonCount }).map((_, i) => (
                            <React.Fragment key={i}>{renderSkeleton()}</React.Fragment>
                        ))
                        : (items.length <= 1 ? items : [...items, ...items, ...items]).map((item, idx) => (
                            <React.Fragment key={`${item.id}-${idx}`}>
                                {renderCard(item, hasMoved)}
                            </React.Fragment>
                        ))
                    }
                </Box>
            </Box>
        </Box>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Home() {
    const { t }                    = useTranslation();
    const navigate                 = useNavigate();
    const { user }                 = useAuth();
    const { currency, convert }    = useCurrency();
    const isManager                = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const theme   = useTheme();
    const isXs    = useMediaQuery(theme.breakpoints.only('xs'));

    const [tours,          setTours]          = useState([]);
    const [loading,        setLoading]        = useState(true);
    const [travels,        setTravels]        = useState([]);
    const [travelsLoading, setTravelsLoading] = useState(true);
    const [heroIndex,      setHeroIndex]      = useState(0);
    const [heroFade,       setHeroFade]       = useState(true);
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef(null);

    const CARD_WIDTH   = isXs ? 260 : 320;
    const IMAGE_HEIGHT = isXs ? 170 : 200;
    const GAP          = isXs ? 16  : 24;

    useEffect(() => {
        axiosPublic.get("/api/tourOffers/top")
            .then(res => setTours(res.data || []))
            .catch(err => console.error("Error retrieving tours", err))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const url = isManager
            ? "/api/bike-travel-offers/view/all?page=0&size=6"
            : "/api/bike-travel-offers/view?page=0&size=6";
        const req = isManager ? axiosAuth.get(url) : axiosPublic.get(url);
        req
            .then(res => setTravels(res.data?.content || []))
            .catch(() => setTravels([]))
            .finally(() => setTravelsLoading(false));
    }, [isManager]);

    useEffect(() => {
        const interval = setInterval(() => {
            setHeroFade(false);
            setTimeout(() => { setHeroIndex(i => (i + 1) % HERO_IMAGES.length); setHeroFade(true); }, 600);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const obs = new ResizeObserver(entries => {
            setContainerWidth(entries[0].contentRect.width);
        });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    const fitsInOneLine = containerWidth > 0 &&
        (tours.length * (CARD_WIDTH + GAP) - GAP) <= containerWidth;

    const getTourIcon = (type) => {
        const sx = { fontSize: '1.6rem' };
        if (type === 'BIKE') return <DirectionsBikeIcon sx={sx} />;
        if (type === 'BUS')  return <DirectionsBusIcon  sx={sx} />;
        if (type === 'WALK') return <DirectionsWalkIcon sx={sx} />;
        return null;
    };

    const getTourTypeLabel = (type) => {
        if (type === 'BIKE') return t('home.type_bike');
        if (type === 'BUS')  return t('home.type_bus');
        if (type === 'WALK') return t('home.type_walk');
        return type;
    };

    const tourCardStyle = {
        width: CARD_WIDTH, minWidth: CARD_WIDTH, display: 'flex', flexDirection: 'column',
        borderRadius: '20px', cursor: 'default',
        transition: 'all 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)',
        userSelect: 'none', backgroundColor: '#ffffff',
        border: '1px solid rgba(196,98,45,0.10)', boxShadow: '0 3px 10px rgba(44,31,20,0.07)', overflow: 'hidden',
        '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(196,98,45,0.18)', borderColor: 'rgba(196,98,45,0.28)' },
        '&:hover .image-container': { padding: 0 },
        '&:hover .tour-image':      { transform: 'scale(1.07)', borderRadius: 0 },
        '&:hover .content-area':    { backgroundColor: TERRACOTTA },
        '&:hover .content-area .tour-title':     { color: 'white' },
        '&:hover .content-area .tour-place':     { color: 'rgba(255,255,255,0.75)' },
        '&:hover .content-area .tour-price':     { color: 'white' },
        '&:hover .content-area .tour-type-icon': { color: 'rgba(255,255,255,0.85)' },
        '&:hover .see-more-btn':  { opacity: 1, transform: 'translateY(0)', pointerEvents: 'auto' },
        '&:hover .type-chip':     { bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.4)' },
    };


    const renderTourCard = (tour, hasMoved) => (
        <Card sx={tourCardStyle}>
            <Box className="image-container" sx={{ padding: '10px', transition: '0.32s', height: IMAGE_HEIGHT, overflow: 'hidden', flexShrink: 0 }}>
                <CardMedia className="tour-image" component="img" height="100%"
                           image={tour.imageUrl} draggable={false}
                           sx={{ objectFit: 'cover', borderRadius: '12px', transition: '0.35s ease',
                               WebkitUserDrag: 'none', height: '100%', width: '100%' }} />
            </Box>
            <CardContent className="content-area" sx={{ flexGrow: 1, display: 'flex',
                flexDirection: 'column', justifyContent: 'space-between', p: 2.5, pt: 2, transition: '0.3s' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Chip className="type-chip" label={getTourTypeLabel(tour.type)}
                              size="small" variant="outlined"
                              sx={{ borderColor: 'rgba(196,98,45,0.3)', color: TERRACOTTA,
                                  fontSize: '0.7rem', fontWeight: 700, transition: '0.3s' }} />
                        <Box className="tour-type-icon" sx={{ color: TERRACOTTA, transition: '0.3s', display: 'flex' }}>
                            {getTourIcon(tour.type)}
                        </Box>
                    </Box>
                    <Typography className="tour-title" variant="h6" fontWeight={800} sx={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        color: WALNUT, lineHeight: 1.25, mb: 0.5, fontSize: '1.15rem', transition: 'color 0.3s' }}>
                        {tour.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <LocationOnIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} className="tour-place" />
                        <Typography className="tour-place" variant="caption"
                                    sx={{ color: 'text.secondary', transition: 'color 0.3s' }}>
                            {tour.place || 'Armenia'}
                        </Typography>
                    </Box>
                    {tour.duration && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.3 }}>
                            <AccessTimeIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                            <Typography variant="caption" className="tour-place"
                                        sx={{ color: 'text.secondary', transition: 'color 0.3s' }}>
                                {tour.duration}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                    <Box>
                        <Typography variant="caption" className="tour-place"
                                    sx={{ color: 'text.secondary', transition: 'color 0.3s' }}>
                            {t('home.from')}
                        </Typography>
                        <Typography className="tour-price" variant="h6" fontWeight={900}
                                    sx={{ color: TERRACOTTA, lineHeight: 1.1, transition: 'color 0.3s' }}>
                            {formatCurrency(convert(tour.basePrice || 0, tour.currency || 'EUR'), currency)}
                        </Typography>
                    </Box>
                    <Button className="see-more-btn" size="small"
                            component="a"
                            href={`/tours/${tour.id}`}
                            onClick={(e) => { if (hasMoved) e.preventDefault(); }}
                            sx={{ opacity: 0, transform: 'translateY(10px)',
                                transition: 'all 0.3s ease-in-out', pointerEvents: 'none',
                                backgroundColor: BEIGE, color: TERRACOTTA, borderRadius: '20px',
                                fontWeight: 800, fontSize: '0.8rem', px: 2, py: 0.8,
                                boxShadow: '0 3px 10px rgba(0,0,0,0.22)',
                                '&:hover': { backgroundColor: '#EDD9C0', transform: 'scale(1.06) translateY(0)' } }}>
                        {t('home.see_details')}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden' }}>
            <Helmet>
                <title>HayTour — Discover Tours in Armenia</title>
                <meta name="description" content="Explore Armenia with HayTour. Book authentic walking, cycling and bus tours to ancient monasteries, mountains and cultural landmarks." />
                <link rel="canonical" href="https://www.haytour.es" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.haytour.es" />
                <meta property="og:title" content="HayTour — Discover Tours in Armenia" />
                <meta property="og:description" content="Explore Armenia with HayTour. Book authentic walking, cycling and bus tours to ancient monasteries, mountains and cultural landmarks." />
                <meta property="og:image" content="https://www.haytour.es/logo512.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="HayTour — Discover Tours in Armenia" />
                <meta name="twitter:description" content="Explore Armenia with HayTour. Book authentic walking, cycling and bus tours to ancient monasteries, mountains and cultural landmarks." />
                <meta name="twitter:image" content="https://www.haytour.es/logo512.png" />
                <script type="application/ld+json">{JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "TravelAgency",
                    "name": "HayTour",
                    "url": "https://www.haytour.es",
                    "logo": "https://www.haytour.es/logo512.png",
                    "description": "Authentic guided tours across Armenia — walking, cycling and bus tours to monasteries, mountains and cultural sites.",
                    "areaServed": { "@type": "Country", "name": "Armenia" }
                })}</script>
            </Helmet>

            {/* ── Hero ─────────────────────────────────────────────────────── */}
            <Box sx={{ height: { xs: '55vh', sm: '65vh' }, minHeight: { xs: 300 }, width: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', inset: 0,
                    backgroundImage: `url("${HERO_IMAGES[heroIndex].url}")`,
                    backgroundSize: 'cover', backgroundPosition: heroIndex === 0 ? 'center' : 'center 30%',
                    opacity: heroFade ? 1 : 0, transition: 'opacity 0.65s ease-in-out',
                    filter: 'blur(1px)', transform: 'scale(1.05)' }} />
                <Box sx={{ position: 'absolute', inset: 0,
                    background: heroIndex === 0
                        ? 'linear-gradient(160deg, rgba(44,31,20,0.72) 0%, rgba(196,98,45,0.38) 55%, rgba(44,31,20,0.65) 100%)'
                        : 'linear-gradient(160deg, rgba(44,31,20,0.55) 0%, rgba(196,98,45,0.20) 55%, rgba(74,103,65,0.25) 100%)' }} />
                <Box sx={{ position: 'absolute', bottom: 18, left: 26, opacity: heroFade ? 0.75 : 0, transition: 'opacity 0.65s ease' }}>
                    <Typography variant="caption" sx={{ color: 'white', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
                        🇦🇲 {HERO_IMAGES[heroIndex].label}
                    </Typography>
                </Box>
                <Box sx={{ position: 'absolute', bottom: 18, right: 26, display: 'flex', gap: 1 }}>
                    {HERO_IMAGES.map((_, i) => (
                        <Box key={i}
                             onClick={() => { setHeroFade(false); setTimeout(() => { setHeroIndex(i); setHeroFade(true); }, 300); }}
                             sx={{ width: i === heroIndex ? 24 : 8, height: 8, borderRadius: 4,
                                 bgcolor: i === heroIndex ? GOLD : 'rgba(255,255,255,0.45)',
                                 transition: 'all 0.4s ease', cursor: 'pointer' }} />
                    ))}
                </Box>
                <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex',
                    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    px: 2, textAlign: 'center' }}>
                    <Typography variant="overline" sx={{ color: GOLD, letterSpacing: '0.25em', fontSize: '0.85rem', mb: 1, fontWeight: 600 }}>
                        🇦🇲 {t('home.overline')}
                    </Typography>
                    <Typography variant="h2" sx={{ color: 'white', fontWeight: 900,
                        fontSize: { xs: '2.2rem', md: '3.8rem' },
                        fontFamily: "'Playfair Display', Georgia, serif",
                        textShadow: '0 2px 24px rgba(0,0,0,0.55)', lineHeight: 1.15, mb: 2 }}>
                        {t('home.hero_title').split('\n').map((line, i) => (
                            <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
                        ))}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 400,
                        fontSize: { xs: '1rem', md: '1.15rem' }, mb: 4, maxWidth: 500 }}>
                        {t('home.hero_sub')}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                        <Button variant="contained" size="large"
                                onClick={() => document.getElementById('travels-section')?.scrollIntoView({ behavior: 'smooth' })}
                                sx={{ background: `linear-gradient(135deg, #1A1A2E 0%, #2A2A4E 100%)`,
                                    color: '#C9973A', px: 4, py: 1.5, borderRadius: '30px',
                                    fontWeight: 700, fontSize: '1rem',
                                    border: '1px solid rgba(201,151,58,0.4)',
                                    boxShadow: '0 8px 24px rgba(26,26,46,0.5)',
                                    '&:hover': { background: `linear-gradient(135deg, #2A2A4E 0%, #3A3A6E 100%)`, transform: 'translateY(-2px)' },
                                    transition: 'all 0.2s' }}>
                            Cycling Journeys
                        </Button>
                        <Button variant="contained" size="large"
                                onClick={() => document.getElementById('tours-section')?.scrollIntoView({ behavior: 'smooth' })}
                                sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                    color: 'white', px: 4, py: 1.5, borderRadius: '30px',
                                    fontWeight: 700, fontSize: '1rem', boxShadow: '0 8px 24px rgba(196,98,45,0.5)',
                                    '&:hover': { background: `linear-gradient(135deg, #D4723D 0%, #AA5A2F 100%)`, transform: 'translateY(-2px)' },
                                    transition: 'all 0.2s' }}>
                            {t('home.tours_btn', 'Tours')}
                        </Button>
                        <Button variant="contained" size="large"
                                onClick={() => document.getElementById('rentals-section')?.scrollIntoView({ behavior: 'smooth' })}
                                sx={{ background: `linear-gradient(135deg, ${FOREST} 0%, #3a5233 100%)`,
                                    color: 'white', px: 4, py: 1.5, borderRadius: '30px',
                                    fontWeight: 700, fontSize: '1rem', boxShadow: '0 8px 24px rgba(74,103,65,0.5)',
                                    '&:hover': { background: `linear-gradient(135deg, #5A7F51 0%, #4a6741 100%)`, transform: 'translateY(-2px)' },
                                    transition: 'all 0.2s' }}>
                            {t('home.rentals_btn', 'Rentals')}
                        </Button>
                    </Stack>
                </Box>
            </Box>

            {/* ══════════════════════════════════════════════════════════════
                CYCLING JOURNEYS — premium, always-visible section
            ══════════════════════════════════════════════════════════════ */}
            <Box id="travels-section"
                 sx={{ position: 'relative', overflow: 'hidden',
                     background: 'linear-gradient(170deg, #0D0D1A 0%, #12122A 45%, #0A1628 100%)' }}>

                {/* Top gold border */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                    background: `linear-gradient(90deg, transparent 0%, ${GOLD} 25%, #E8B84B 50%, ${GOLD} 75%, transparent 100%)` }} />

                {/* Faint watermark bike */}
                <Box sx={{ position: 'absolute', right: { xs: -80, md: -40 }, bottom: -40,
                    opacity: 0.035, pointerEvents: 'none', lineHeight: 0 }}>
                    <DirectionsBikeIcon sx={{ fontSize: { xs: '22rem', md: '34rem' }, color: GOLD }} />
                </Box>

                {/* ── Header strip ── */}
                <Box sx={{ px: { xs: 2.5, sm: 4, md: 8 }, pt: { xs: 7, md: 9 }, pb: 5,
                    position: 'relative', zIndex: 1 }}>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 3 }}>

                        {/* Left: labels + title */}
                        <Box sx={{ maxWidth: 560 }}>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                                <Box sx={{ width: 32, height: 2,
                                    background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
                                <Typography variant="overline"
                                            sx={{ color: GOLD, letterSpacing: '0.32em', fontWeight: 700,
                                                fontSize: '0.72rem', lineHeight: 1 }}>
                                    Signature Experiences
                                </Typography>
                            </Stack>

                            <Typography variant="h3" fontWeight={900}
                                        sx={{ color: 'white',
                                            fontFamily: "'Playfair Display', Georgia, serif",
                                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                            lineHeight: 1.1, mb: 1.5,
                                            '& em': { color: GOLD, fontStyle: 'normal' } }}>
                                Multi-day <em>Cycling</em> Journeys
                            </Typography>

                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', lineHeight: 1.8,
                                fontSize: { xs: '0.9rem', md: '1rem' }, maxWidth: 460 }}>
                                Handcrafted routes through Armenia's mountains, gorges, and ancient roads.
                                Fully equipped. Expertly guided. Unforgettable.
                            </Typography>
                        </Box>

                        {/* Right: action buttons */}
                        <Stack spacing={1.5} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                            <Button variant="contained" endIcon={<EastIcon />}
                                    component="a" href="/bike-travel-offers"
                                    sx={{ background: `linear-gradient(135deg, ${GOLD} 0%, #A07820 100%)`,
                                        color: '#0D0D1A', borderRadius: '30px', fontWeight: 800,
                                        px: 3.5, py: 1.3, fontSize: '0.9rem',
                                        boxShadow: `0 6px 24px rgba(201,151,58,0.45)`,
                                        '&:hover': { background: `linear-gradient(135deg, #E8B84B 0%, #B08830 100%)`,
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 10px 32px rgba(201,151,58,0.55)` },
                                        transition: 'all 0.22s' }}>
                                Browse all journeys
                            </Button>
                            {isManager && (
                                <Button variant="outlined" startIcon={<AddCircleOutlineIcon />}
                                        component="a" href="/bike-travel-offers/create"
                                        sx={{ borderColor: 'rgba(201,151,58,0.45)', color: GOLD,
                                            borderRadius: '30px', fontWeight: 700, px: 3, py: 1.1,
                                            '&:hover': { borderColor: GOLD,
                                                bgcolor: 'rgba(201,151,58,0.1)',
                                                transform: 'translateY(-1px)' },
                                            transition: 'all 0.22s' }}>
                                    Create journey
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Box>

                {/* ── Cards area ── */}
                <Box sx={{ px: { xs: 2.5, sm: 4, md: 8 }, pb: { xs: 7, md: 9 },
                    position: 'relative', zIndex: 1 }}>

                    {/* Loading skeletons */}
                    {travelsLoading && (
                        <Box sx={{ display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.9fr 1fr 1fr' },
                            gap: 2.5 }}>
                            {[0, 1, 2].map(i => (
                                <Box key={i} sx={{
                                    gridRow: i === 0 ? { md: 'span 2' } : 'auto',
                                    minHeight: i === 0 ? { md: 520 } : 240,
                                    borderRadius: 3, overflow: 'hidden',
                                    bgcolor: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(201,151,58,0.08)',
                                    animation: 'pulse 1.8s ease-in-out infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.5 },
                                    },
                                }} />
                            ))}
                        </Box>
                    )}

                    {/* Empty state */}
                    {!travelsLoading && travels.length === 0 && (
                        <Box sx={{ py: { xs: 8, md: 10 }, textAlign: 'center',
                            border: '1px dashed rgba(201,151,58,0.2)', borderRadius: 3 }}>
                            <DirectionsBikeIcon sx={{ fontSize: '3.5rem', color: 'rgba(201,151,58,0.25)', mb: 2 }} />
                            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600, mb: 1 }}>
                                Journeys coming soon
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                                Our cycling routes are being prepared. Check back shortly.
                            </Typography>
                            {isManager && (
                                <Button variant="contained" startIcon={<AddCircleOutlineIcon />}
                                        component="a" href="/bike-travel-offers/create"
                                        sx={{ mt: 3,
                                            background: `linear-gradient(135deg, ${GOLD} 0%, #A07820 100%)`,
                                            color: '#0D0D1A', borderRadius: 2, fontWeight: 800 }}>
                                    Create the first journey
                                </Button>
                            )}
                        </Box>
                    )}

                    {/* Mosaic grid */}
                    {!travelsLoading && travels.length > 0 && (() => {
                        const featured = travels[0];
                        const rest     = travels.slice(1, 5);

                        const DIFF_COLORS = {
                            EASY:   { bg: 'rgba(46,125,50,0.85)',   color: '#fff' },
                            MEDIUM: { bg: 'rgba(230,81,0,0.85)',    color: '#fff' },
                            HARD:   { bg: 'rgba(183,28,28,0.85)',   color: '#fff' },
                        };

                        const CardContent = ({ offer, tall }) => {
                            const diff = DIFF_COLORS[offer.difficulty] || {};
                            return (
                                <Box component="a" href={`/bike-travel-offers/${offer.id}`}
                                     sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', textDecoration: 'none', color: 'inherit', display: 'block',
                                         cursor: 'pointer', height: '100%',
                                         minHeight: tall ? { xs: 340, sm: 420, md: 'unset' } : 230,
                                         border: '1px solid rgba(201,151,58,0.15)',
                                         transition: 'all 0.38s cubic-bezier(0.34,1.2,0.64,1)',
                                         '&:hover': {
                                             transform: 'translateY(-5px)',
                                             boxShadow: '0 28px 64px rgba(0,0,0,0.65)',
                                             borderColor: 'rgba(201,151,58,0.55)',
                                         },
                                         '&:hover .tc-img':  { transform: 'scale(1.08)' },
                                         '&:hover .tc-cta':  { opacity: 1, transform: 'translateY(0)' },
                                     }}>

                                    {/* Image */}
                                    {offer.imageUrl ? (
                                        <Box component="img" src={offer.imageUrl} alt={offer.title}
                                             className="tc-img"
                                             sx={{ position: 'absolute', inset: 0,
                                                 width: '100%', height: '100%',
                                                 objectFit: 'cover', transition: '0.5s ease' }} />
                                    ) : (
                                        <Box sx={{ position: 'absolute', inset: 0,
                                            background: 'linear-gradient(135deg, #1A1A2E 0%, #0F3460 100%)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <DirectionsBikeIcon sx={{ fontSize: '6rem', color: GOLD, opacity: 0.15 }} />
                                        </Box>
                                    )}

                                    {/* Gradient */}
                                    <Box sx={{ position: 'absolute', inset: 0,
                                        background: tall
                                            ? 'linear-gradient(to top, rgba(5,5,20,0.95) 0%, rgba(5,5,20,0.45) 45%, rgba(5,5,20,0.1) 100%)'
                                            : 'linear-gradient(to top, rgba(5,5,20,0.92) 0%, rgba(5,5,20,0.3) 100%)' }} />

                                    {/* Content */}
                                    <Box sx={{ position: 'absolute', inset: 0,
                                        display: 'flex', flexDirection: 'column',
                                        justifyContent: 'flex-end', p: tall ? 3.5 : 2.5 }}>

                                        {/* Top badges row */}
                                        <Stack direction="row" spacing={0.8} mb={1.5} flexWrap="wrap">
                                            {offer.difficulty && (
                                                <Box component="span" sx={{
                                                    px: 1.2, py: 0.3, borderRadius: '6px',
                                                    bgcolor: diff.bg, color: diff.color,
                                                    fontSize: '0.65rem', fontWeight: 800,
                                                    letterSpacing: '0.06em', backdropFilter: 'blur(4px)',
                                                }}>
                                                    {offer.difficulty}
                                                </Box>
                                            )}
                                            {offer.distance && (
                                                <Stack direction="row" spacing={0.4} alignItems="center"
                                                       sx={{ px: 1, py: 0.3, borderRadius: '6px',
                                                           bgcolor: 'rgba(201,151,58,0.2)',
                                                           border: '1px solid rgba(201,151,58,0.35)' }}>
                                                    <StraightenIcon sx={{ fontSize: '0.7rem', color: GOLD }} />
                                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: GOLD }}>
                                                        {offer.distance} km
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {offer.duration && (
                                                <Box component="span" sx={{
                                                    px: 1, py: 0.3, borderRadius: '6px',
                                                    bgcolor: 'rgba(255,255,255,0.12)',
                                                    border: '1px solid rgba(255,255,255,0.25)',
                                                    fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)',
                                                }}>
                                                    {offer.duration}d
                                                </Box>
                                            )}
                                        </Stack>

                                        {/* Title */}
                                        <Typography fontWeight={900}
                                                    sx={{ color: 'white', lineHeight: 1.2, mb: 0.8,
                                                        fontFamily: "'Playfair Display', Georgia, serif",
                                                        fontSize: tall ? { xs: '1.4rem', md: '1.75rem' } : '1.05rem',
                                                        textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}>
                                            {offer.title}
                                        </Typography>

                                        {/* Meta row */}
                                        <Stack direction="row" spacing={1.5} alignItems="center" mb={tall ? 2 : 1.5}>
                                            {offer.region && (
                                                <Stack direction="row" spacing={0.4} alignItems="center">
                                                    <LocationOnIcon sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }} />
                                                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                                                        {offer.region}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {offer.basePrice > 0 && (
                                                <Typography sx={{ ml: 'auto', fontSize: tall ? '1.05rem' : '0.85rem',
                                                    fontWeight: 900, color: GOLD }}>
                                                    {t('home.from', 'From')} {formatCurrency(convert(offer.basePrice || 0, offer.currency || 'EUR'), currency)}
                                                </Typography>
                                            )}
                                        </Stack>


                                        {/* CTA button */}
                                        <Button className="tc-cta" size="small" endIcon={<EastIcon />}
                                                sx={{ alignSelf: 'flex-start',
                                                    opacity: { xs: 1, md: 0 },
                                                    transform: { xs: 'none', md: 'translateY(8px)' },
                                                    transition: 'all 0.3s ease',
                                                    bgcolor: GOLD, color: '#0D0D1A',
                                                    borderRadius: '20px', fontWeight: 800,
                                                    fontSize: '0.78rem', px: 2.2, py: 0.75,
                                                    boxShadow: '0 4px 16px rgba(201,151,58,0.4)',
                                                    '&:hover': { bgcolor: '#E8B84B',
                                                        boxShadow: '0 6px 20px rgba(201,151,58,0.55)' } }}>
                                            Explore route
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        };

                        /* Single offer — full-width tall card */
                        if (travels.length === 1) {
                            return (
                                <Box sx={{ height: { xs: 380, md: 500 } }}>
                                    <CardContent offer={featured} tall />
                                </Box>
                            );
                        }

                        /* Two offers — side by side */
                        if (travels.length === 2) {
                            return (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 2.5, height: { sm: 460 } }}>
                                    {travels.map(o => <CardContent key={o.id} offer={o} tall />)}
                                </Box>
                            );
                        }

                        /* 3+ offers — editorial mosaic */
                        return (
                            <Box sx={{ display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.85fr 1fr' },
                                gridTemplateRows:    { md: '1fr 1fr' },
                                gap: 2.5,
                                minHeight: { md: 540 } }}>

                                {/* Featured — spans both rows on md+ */}
                                <Box sx={{ gridRow: { md: 'span 2' } }}>
                                    <CardContent offer={featured} tall />
                                </Box>

                                {/* Side cards */}
                                {rest.slice(0, 2).map(o => (
                                    <CardContent key={o.id} offer={o} tall={false} />
                                ))}

                                {/* If 5+ offers: show a "view all" tile as the 4th slot */}
                                {rest.length >= 3 && (
                                    <Box component="a" href="/bike-travel-offers"
                                         sx={{ borderRadius: 3, overflow: 'hidden', cursor: 'pointer', textDecoration: 'none', color: 'inherit',
                                             minHeight: 230,
                                             border: '1px dashed rgba(201,151,58,0.3)',
                                             display: 'flex', flexDirection: 'column',
                                             alignItems: 'center', justifyContent: 'center', gap: 1.5,
                                             bgcolor: 'rgba(201,151,58,0.04)',
                                             transition: 'all 0.3s',
                                             '&:hover': { bgcolor: 'rgba(201,151,58,0.09)',
                                                 borderColor: 'rgba(201,151,58,0.55)',
                                                 transform: 'translateY(-4px)' } }}>
                                        <Typography sx={{ color: GOLD, fontWeight: 900,
                                            fontSize: '2rem', lineHeight: 1 }}>
                                            +{travels.length - 3}
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', fontWeight: 600 }}>
                                            more journeys
                                        </Typography>
                                        <Button size="small" endIcon={<EastIcon />}
                                                sx={{ color: GOLD, borderColor: 'rgba(201,151,58,0.4)',
                                                    border: '1px solid', borderRadius: '20px',
                                                    fontWeight: 700, px: 2, fontSize: '0.78rem',
                                                    '&:hover': { bgcolor: 'rgba(201,151,58,0.12)' } }}>
                                            View all
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        );
                    })()}
                </Box>

                {/* Bottom gold border */}
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
                    background: `linear-gradient(90deg, transparent 0%, ${GOLD} 30%, ${GOLD} 70%, transparent 100%)`,
                    opacity: 0.35 }} />
            </Box>

            {/* ── Featured Tours ────────────────────────────────────────────── */}
            <Container ref={containerRef} id="tours-section" maxWidth={false} sx={{ py: 8, px: { xs: 2, sm: 4, md: 8 }, position: 'relative', overflow: 'hidden' }}>
                {/* Background image */}
                <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 0,
                    backgroundImage: 'url("https://explorearmenia.am/images/2025/09/20/ea-background-min.webp")',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    opacity: 0.10, pointerEvents: 'none',
                }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                    <Box>
                        <Typography variant="overline" sx={{ color: FOREST, letterSpacing: '0.2em', fontWeight: 700 }}>
                            {t('home.offer_overline')}
                        </Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ fontFamily: "'Playfair Display', Georgia, serif",
                            color: WALNUT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box component="span" sx={{ display: 'inline-block', width: 6, height: 36, borderRadius: 3,
                                background: `linear-gradient(180deg, ${FOREST} 0%, #6A8F61 100%)`, flexShrink: 0 }} />
                            {t('home.featured')}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        <Button variant="outlined" endIcon={<EastIcon />}
                                component="a" href="/tours"
                                sx={{ borderColor: 'rgba(196,98,45,0.4)', color: TERRACOTTA, borderRadius: '20px',
                                    fontWeight: 700, px: 2.5,
                                    '&:hover': { borderColor: TERRACOTTA, bgcolor: 'rgba(196,98,45,0.06)' } }}>
                            {t('home.see_all_tours', 'See all tours')}
                        </Button>
                        {isManager && (
                            <Button variant="contained" startIcon={<AddCircleOutlineIcon />}
                                    component="a" href="/tours/create"
                                    sx={{ background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                        color: 'white', borderRadius: 2, fontWeight: 700, px: 2.5, py: 1,
                                        boxShadow: '0 4px 14px rgba(196,98,45,0.35)',
                                        '&:hover': { background: `linear-gradient(135deg, #D4723D 0%, #AA5A2F 100%)`,
                                            transform: 'translateY(-1px)', boxShadow: '0 6px 18px rgba(196,98,45,0.45)' },
                                        transition: 'all 0.2s' }}>
                                {t('home.create_tour')}
                            </Button>
                        )}
                    </Stack>
                </Box>

                {loading ? (
                    <HorizontalCarousel
                        items={[]}
                        loading={loading}
                        renderCard={renderTourCard}
                        renderSkeleton={() => <TourCardSkeleton cardWidth={CARD_WIDTH} imageHeight={IMAGE_HEIGHT} />}
                        cardWidth={CARD_WIDTH}
                        gap={GAP}
                    />
                ) : fitsInOneLine ? (
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        {tours.map(tour => (
                            <Grid item xs={12} sm={6} md={4} key={tour.id}>
                                {renderTourCard(tour, false)}
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <>
                        <HorizontalCarousel
                            items={tours}
                            loading={loading}
                            renderCard={renderTourCard}
                            renderSkeleton={() => <TourCardSkeleton cardWidth={CARD_WIDTH} imageHeight={IMAGE_HEIGHT} />}
                            cardWidth={CARD_WIDTH}
                            gap={GAP}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            {t('home.drag')}
                        </Typography>
                    </>
                )}
                </Box>
            </Container>

            {/* ── Bicycle Rentals — full-width promo card ─────────────────── */}
            <Box id="rentals-section" component="a" href="/rentals"
                 sx={{ position: 'relative', overflow: 'hidden', textDecoration: 'none', color: 'inherit', display: 'block',
                     height: { xs: 320, sm: 420, md: 500 },
                     cursor: 'pointer',
                     '&:hover .rental-bg': { transform: 'scale(1.04)' },
                 }}>

                {/* Background gradient */}
                <Box className="rental-bg"
                     sx={{ position: 'absolute', inset: 0,
                         background: 'linear-gradient(135deg, #1A3A2A 0%, #2A5E3A 35%, #3D7A50 65%, #4A6741 100%)',
                         transition: 'transform 0.55s ease' }} />

                {/* Subtle diagonal stripe texture */}
                <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04,
                    backgroundImage: 'repeating-linear-gradient(45deg, white 0, white 1px, transparent 0, transparent 50%)',
                    backgroundSize: '22px 22px' }} />

                {/* Gradient overlay — heavy left, fade right */}
                <Box sx={{ position: 'absolute', inset: 0,
                    background: 'linear-gradient(105deg, rgba(10,28,18,0.97) 0%, rgba(10,28,18,0.82) 30%, rgba(10,28,18,0.45) 60%, rgba(10,28,18,0.05) 100%)' }} />

                {/* Watermark bike */}
                <Box sx={{ position: 'absolute', right: { xs: -70, md: 20 }, bottom: -30,
                    opacity: 0.07, pointerEvents: 'none' }}>
                    <DirectionsBikeIcon sx={{ fontSize: { xs: '20rem', md: '30rem' }, color: 'white' }} />
                </Box>

                {/* Content */}
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    px: { xs: 4, sm: 7, md: 12 } }}>
                    <Box sx={{ maxWidth: { xs: '100%', sm: '65%', md: '52%' } }}>

                        <Typography variant="overline"
                                    sx={{ color: GOLD, letterSpacing: '0.28em', fontWeight: 700,
                                        fontSize: '0.73rem', display: 'block', mb: 2 }}>
                            Explore at your own pace
                        </Typography>

                        <Typography variant="h3" fontWeight={900}
                                    sx={{ color: 'white', lineHeight: 1.15, mb: 2,
                                        fontFamily: "'Playfair Display', Georgia, serif",
                                        fontSize: { xs: '1.8rem', sm: '2.4rem', md: '3rem' },
                                        textShadow: '0 2px 20px rgba(0,0,0,0.4)' }}>
                            Rent a Bike
                        </Typography>

                        <Typography sx={{ color: 'rgba(255,255,255,0.68)',
                            fontSize: { xs: '0.92rem', md: '1.02rem' },
                            lineHeight: 1.85, mb: 4, maxWidth: 420 }}>
                            Choose your own schedule. Pick your start time, duration, and bike type.
                            Discover Armenia's landscapes freely — no fixed tours, just open roads.
                        </Typography>

                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            <Button variant="contained" endIcon={<EastIcon />}
                                    sx={{ background: `linear-gradient(135deg, ${FOREST} 0%, #3a5233 100%)`,
                                        color: 'white', borderRadius: '30px', fontWeight: 800,
                                        px: 3.5, py: 1.3, fontSize: '0.95rem',
                                        boxShadow: '0 6px 24px rgba(74,103,65,0.55)',
                                        '&:hover': { background: `linear-gradient(135deg, #5A7F51 0%, #4a6741 100%)`,
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 10px 32px rgba(74,103,65,0.65)' },
                                        transition: 'all 0.22s' }}>
                                Rent a Bike
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
